import React, { useState, useRef } from "react";
import { privateAxios } from "../utils/axios";

const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
  </svg>
);

const Section = ({ title, children, count }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-100 bg-slate-50">
      <h3 className="font-semibold text-slate-700 text-sm">{title}</h3>
      {count !== undefined && (
        <span className="ml-auto text-xs font-medium bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
          {count} record{count !== 1 ? "s" : ""}
        </span>
      )}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-1 py-2 border-b border-slate-100 last:border-0">
    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide sm:w-44 shrink-0">{label}</span>
    <span className="text-sm text-slate-800">{value || <span className="text-slate-400 italic">N/A</span>}</span>
  </div>
);

const Badge = ({ text, color = "blue" }) => {
  const colors = {
    blue: "bg-blue-100 text-blue-700",
    green: "bg-emerald-100 text-emerald-700",
    yellow: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors[color]}`}>
      {text}
    </span>
  );
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatCurrency(amount) {
  if (amount === undefined || amount === null) return null;
  return `৳ ${Number(amount).toLocaleString()}`;
}

function ProductTracking() {
  const [sku, setSku] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const handleSearch = async (e) => {
    e?.preventDefault();
    const trimmed = sku.trim();
    if (!trimmed) return;

    setLoading(true);
    setError("");
    setResult(null);

    try {
      const res = await privateAxios.get(`/tracking?sku=${encodeURIComponent(trimmed)}`);
      if (res.data.success) {
        setResult(res.data.data);
      } else {
        setError(res.data.message || "Something went wrong.");
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || "No product found for this SKU."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSku("");
    setResult(null);
    setError("");
    inputRef.current?.focus();
  };

  const { productInfo, productStores = [], purchaseOrders = [], expenses = [] } = result || {};

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Product Tracking</h1>
          <p className="text-sm text-slate-500 mt-1">
            Search by SKU to view product store info, purchase orders, and expenses.
          </p>
        </div>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="flex gap-3">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-slate-400">
              <SearchIcon />
            </div>
            <input
              ref={inputRef}
              type="text"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              placeholder="Enter SKU code..."
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
              autoFocus
            />
          </div>
          <button
            type="submit"
            disabled={loading || !sku.trim()}
            className="px-6 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {loading ? "Searching..." : "Search"}
          </button>
          {(result || error) && (
            <button
              type="button"
              onClick={handleClear}
              className="px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Clear
            </button>
          )}
        </form>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            {error}
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="space-y-5">

            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { label: "Store Records", value: productStores.length, color: "text-blue-600" },
                { label: "Purchase Orders", value: purchaseOrders.length, color: "text-emerald-600" },
                { label: "Expenses", value: expenses.length, color: "text-amber-600" },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-white rounded-xl border border-slate-200 px-5 py-4 flex items-center gap-4">
                  <span className={`text-3xl font-bold ${color}`}>{value}</span>
                  <span className="text-sm text-slate-500 font-medium">{label}</span>
                </div>
              ))}
            </div>

            {/* 1. Product Info */}
            <Section title="Product Information">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                <div>
                  <InfoRow label="Name" value={productInfo.name} />
                  <InfoRow label="SKU" value={<Badge text={productInfo.sku} color="blue" />} />
                  <InfoRow label="Category" value={productInfo.category} />
                  <InfoRow label="Unit" value={productInfo.unit} />
                </div>
                <div>
                  <InfoRow label="Price" value={formatCurrency(productInfo.price)} />
                  <InfoRow label="Warranty Expires" value={formatDate(productInfo.warrantyExpireDate)} />
                  <InfoRow label="Description" value={productInfo.description} />
                  <InfoRow label="Total Store Records" value={productInfo.totalStoreRecords} />
                </div>
              </div>
            </Section>

            {/* 2. Product Store Records */}
            <Section title="Product Store Records" count={productStores.length}>
              {productStores.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No store records found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-slate-500 uppercase tracking-wide border-b border-slate-100">
                        <th className="pb-2 pr-4 font-medium">#</th>
                        <th className="pb-2 pr-4 font-medium">SKU</th>
                        <th className="pb-2 pr-4 font-medium">Name</th>
                        <th className="pb-2 pr-4 font-medium">Price</th>
                        <th className="pb-2 pr-4 font-medium">Warranty</th>
                        <th className="pb-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {productStores.map((ps, i) => (
                        <tr key={ps._id} className="hover:bg-slate-50 transition-colors">
                          <td className="py-2 pr-4 text-slate-400">{i + 1}</td>
                          <td className="py-2 pr-4 font-mono text-blue-600">{ps.sku}</td>
                          <td className="py-2 pr-4 text-slate-700">{ps.name}</td>
                          <td className="py-2 pr-4 text-slate-700">{formatCurrency(ps.price)}</td>
                          <td className="py-2 pr-4 text-slate-500">{formatDate(ps.warrantyExpireDate)}</td>
                          <td className="py-2">
                            <Badge text={ps.hide ? "Hidden" : "Active"} color={ps.hide ? "yellow" : "green"} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>

            {/* 3. Purchase Orders */}
            <Section title="Purchase Orders" count={purchaseOrders.length}>
              {purchaseOrders.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No purchase orders found.</p>
              ) : (
                <div className="space-y-4">
                  {purchaseOrders.map((po, i) => (
                    <div key={po._id} className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                        <span className="text-xs font-semibold text-slate-500">#{i + 1}</span>
                        {po.invoiceNumber && <Badge text={`Invoice: ${po.invoiceNumber}`} color="blue" />}
                        {po.memoNumber && <Badge text={`Memo: ${po.memoNumber}`} color="yellow" />}
                        <span className="ml-auto text-xs text-slate-400">{formatDate(po.purchaseDate)}</span>
                      </div>
                      <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                        <div>
                          <InfoRow label="Supplier" value={po.supplier?.name} />
                          <InfoRow label="Purchased By" value={po.purchaseByName} />
                          <InfoRow label="Phone" value={po.purchaseByPhone} />
                        </div>
                        <div>
                          <InfoRow label="Total Amount" value={formatCurrency(po.totalAmount)} />
                          <InfoRow label="Sub Total" value={formatCurrency(po.subTotal)} />
                          <InfoRow label="VAT" value={formatCurrency(po.vat)} />
                          <InfoRow label="Discount" value={formatCurrency(po.discount)} />
                        </div>
                      </div>
                      {/* Products in this PO that match the SKU */}
                      {po.products?.filter((p) => p.skus?.includes(sku.trim())).map((p, j) => (
                        <div key={j} className="px-4 pb-3">
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Matched Line Item</p>
                          <div className="flex flex-wrap gap-4 text-sm text-slate-700 bg-blue-50 rounded-lg px-3 py-2">
                            <span>Qty: <strong>{p.quantity}</strong></span>
                            <span>Purchase Price: <strong>{formatCurrency(p.purchasePrice)}</strong></span>
                            {p.warrantyExpireDate && <span>Warranty: <strong>{formatDate(p.warrantyExpireDate)}</strong></span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* 4. Expenses */}
            <Section title="Expenses / Sales" count={expenses.length}>
              {expenses.length === 0 ? (
                <p className="text-sm text-slate-400 italic">No expense records found.</p>
              ) : (
                <div className="space-y-4">
                  {expenses.map((exp, i) => (
                    <div key={exp._id} className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                        <span className="text-xs font-semibold text-slate-500">#{i + 1}</span>
                        {exp.invoiceNumber && <Badge text={`Invoice: ${exp.invoiceNumber}`} color="blue" />}
                        <span className="ml-auto text-xs text-slate-400">{formatDate(exp.expenseDate)}</span>
                      </div>
                      <div className="px-4 py-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                        <div>
                          <InfoRow label="Receiver" value={exp.receiverName} />
                          <InfoRow label="Phone" value={exp.receiverPhone} />
                          <InfoRow label="Address" value={exp.receiverAddress} />
                        </div>
                        <div>
                          <InfoRow label="Total Amount" value={formatCurrency(exp.totalAmount)} />
                          <InfoRow label="Sub Total" value={formatCurrency(exp.subTotal)} />
                          <InfoRow label="Discount" value={formatCurrency(exp.discount)} />
                          <InfoRow label="Created By" value={exp.createBy?.name} />
                        </div>
                      </div>
                      {exp.products?.filter((p) => p.skus?.includes(sku.trim())).map((p, j) => (
                        <div key={j} className="px-4 pb-3">
                          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Matched Line Item</p>
                          <div className="flex flex-wrap gap-4 text-sm text-slate-700 bg-amber-50 rounded-lg px-3 py-2">
                            <span>Qty: <strong>{p.quantity}</strong></span>
                            <span>Price: <strong>{formatCurrency(p.price)}</strong></span>
                            {p.warrantyExpireDate && <span>Warranty: <strong>{formatDate(p.warrantyExpireDate)}</strong></span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </Section>

          </div>
        )}
      </div>
    </div>
  );
}

export default ProductTracking;
